import { LocalDate, YearMonth } from "@js-joda/core";
import { extractPageParams } from "@jsonapi-serde/client";
import AddIcon from "@mui/icons-material/Add";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import {
    Alert,
    Badge,
    Box,
    Container,
    Divider,
    IconButton,
    Popover,
    Stack,
    Tab,
    Tabs,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import dayjs from "dayjs";
import { bindPopover, bindTrigger, usePopupState } from "material-ui-popup-state/hooks";
import { type ReactNode, useMemo, useState } from "react";
import { z } from "zod/mini";
import { ButtonLink } from "@/components/Link";
import IconButtonLink from "@/components/Link/IconButtonLink.tsx";
import { useQueryOptionsFactory } from "@/queries";
import AuctionCard from "@/routes/manage/auctions/-components/AuctionCard.tsx";

type RangeMode = "year" | "month";

const Root = (): ReactNode => {
    const qof = useQueryOptionsFactory();
    const { pageParams, status, startsAtRange: startsAtRangeIso } = Route.useSearch();
    const startsAtRange = useMemo(() => {
        if (!startsAtRangeIso) {
            return null;
        }

        return startsAtRangeIso.length === 4
            ? Number.parseInt(startsAtRangeIso, 10)
            : YearMonth.parse(startsAtRangeIso);
    }, [startsAtRangeIso]);
    const startsAtRangeDayjs = useMemo(() => {
        if (!startsAtRange) {
            return null;
        }

        return startsAtRange instanceof YearMonth
            ? dayjs(startsAtRange.atDay(1).atTime(0, 0, 0).toString())
            : dayjs(LocalDate.of(startsAtRange, 1, 1).atTime(0, 0, 0).toString());
    }, [startsAtRange]);
    const [rangeMode, setRangeMode] = useState<RangeMode>(() => {
        return startsAtRange instanceof YearMonth ? "month" : "year";
    });
    const navigate = Route.useNavigate();
    const page = useSuspenseQuery(qof.auction.list({ pageParams, status, startsAtRange })).data;
    const filterPopupState = usePopupState({
        variant: "popover",
        popupId: "filterPopover",
    });

    const pagination = extractPageParams(page.links ?? {});

    return (
        <Container>
            <Box sx={{ display: "flex", mb: 2, alignItems: "center" }}>
                <Typography variant="h5" sx={{ flexGrow: 1 }}>
                    Land Auctions
                </Typography>

                <ButtonLink
                    to={"/manage/auctions/create"}
                    search
                    variant="contained"
                    startIcon={<AddIcon />}
                    sx={{ display: { xs: "none", sm: "flex" } }}
                >
                    New Auction
                </ButtonLink>

                <IconButtonLink
                    to={"/manage/auctions/create"}
                    search
                    sx={{ display: { xs: "flex", sm: "none" } }}
                    edge="end"
                >
                    <AddIcon />
                </IconButtonLink>
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                <Stack
                    spacing={2}
                    direction="row"
                    sx={{
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <Tabs
                        value={status}
                        onChange={(_event, value) => {
                            navigate({
                                search: {
                                    status: value as "active" | "completed",
                                    startsAtRange: startsAtRangeIso,
                                    pageParams: { size: "10" },
                                },
                            });
                        }}
                    >
                        <Tab label="Active" value="active" />
                        <Tab label="Completed" value="completed" />
                    </Tabs>

                    <IconButton {...bindTrigger(filterPopupState)}>
                        <Badge variant="dot" color="primary" invisible={!startsAtRange}>
                            <FilterAltIcon />
                        </Badge>
                    </IconButton>

                    <Popover
                        {...bindPopover(filterPopupState)}
                        anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "right",
                        }}
                        transformOrigin={{
                            vertical: "top",
                            horizontal: "right",
                        }}
                    >
                        <Stack sx={{ p: 2 }} spacing={2}>
                            <ToggleButtonGroup
                                value={rangeMode}
                                exclusive
                                onChange={(_, newMode) => {
                                    setRangeMode(newMode);
                                    navigate({
                                        search: {
                                            status,
                                            startsAtRange: null,
                                            pageParams: { size: "10" },
                                        },
                                    });
                                }}
                                fullWidth
                            >
                                <ToggleButton value="year">Year</ToggleButton>
                                <ToggleButton value="month">Month</ToggleButton>
                            </ToggleButtonGroup>

                            <DatePicker
                                value={startsAtRangeDayjs}
                                views={rangeMode === "year" ? ["year"] : ["month", "year"]}
                                slotProps={{
                                    actionBar: { actions: ["accept", "clear"] },
                                    field: { readOnly: true },
                                }}
                                onAccept={(value) => {
                                    const startsAtRange = value
                                        ? rangeMode === "year"
                                            ? value.year().toString()
                                            : YearMonth.of(
                                                  value.year(),
                                                  value.month() + 1,
                                              ).toString()
                                        : null;

                                    navigate({
                                        search: {
                                            status,
                                            startsAtRange,
                                            pageParams: { size: "10" },
                                        },
                                    });

                                    filterPopupState.close();
                                }}
                            />
                        </Stack>
                    </Popover>
                </Stack>
            </Box>

            {page.data.length === 0 && <Alert severity="info">No auctions founds.</Alert>}

            {page.data.length > 0 && (
                <Stack spacing={2}>
                    {page.data.map((auction) => (
                        <AuctionCard auction={auction} key={auction.id} />
                    ))}
                </Stack>
            )}

            {(pagination.first || pagination.next) && (
                <>
                    <Divider sx={{ mt: 2, mb: 1 }} />
                    <IconButtonLink
                        to="."
                        search={{ status, pageParams: pagination.first }}
                        disabled={!pagination.first}
                        onClick={() => {
                            window.scroll({ top: 0 });
                        }}
                    >
                        <FirstPageIcon />
                    </IconButtonLink>

                    <IconButtonLink
                        to="."
                        search={{ status, pageParams: pagination.prev }}
                        disabled={!pagination.prev}
                        onClick={() => {
                            window.scroll({ top: 0 });
                        }}
                    >
                        <KeyboardArrowLeftIcon />
                    </IconButtonLink>
                    <IconButtonLink
                        to="."
                        search={{ status, pageParams: pagination.next }}
                        disabled={!pagination.next}
                        onClick={() => {
                            window.scroll({ top: 0 });
                        }}
                    >
                        <KeyboardArrowRightIcon />
                    </IconButtonLink>
                </>
            )}

            <Outlet />
        </Container>
    );
};

const searchSchema = z.object({
    pageParams: z._default(z.catch(z.record(z.string(), z.string()), { size: "10" }), {
        size: "10",
    }),
    status: z._default(z.catch(z.enum(["active", "completed"]), "active"), "active"),
    startsAtRange: z._default(
        z.catch(z.nullable(z.string().check(z.regex(/^\d{4}(?:-\d{2})?$/))), null),
        null,
    ),
});

export const Route = createFileRoute("/manage/auctions")({
    component: Root,
    validateSearch: searchSchema,
    loaderDeps: ({ search: { pageParams, status } }) => ({ pageParams, status }),
    loader: async ({ context, deps }) => {
        await context.queryClient.ensureQueryData(
            context.qof.auction.list({
                pageParams: deps.pageParams,
                status: deps.status,
            }),
        );
    },
});
